/**
 * 还款流程版本工具类
 * 用于获取还款流程版本，并存储到localStorage
 * 用于在还款流程中，根据还款流程版本，判断是否需要走新还款流程
 * 当实验推全后，需要删除该工具类
 * ⚠️注意：此处的AppFinanceH5Web_hit_RepaymentVersion_experiment的值在本项目存储，但是是在新项目获取
 */
import store, { updateModuleState } from '@/store';
import platform from '@/shared/utils/platform';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import { getRepaymentPageExperiment } from '@/api/homeV2Status';

/** 路由名称映射配置,与新项目一致 */
export const routeNameMapping = {
  [NewVersionEnum.RepayBills]: NewVersionEnum.RepaymentBills,
  [NewVersionEnum.RepayPlans]: NewVersionEnum.RepaymentPlan,
};

/**
 * 获取还款流程版本并更新state和localStorage
 */
export const getAndStoreRepaymentVersion = async (): Promise<boolean> => {
  const { hitRepaymentExperiment } = store.state.base;
  let result = hitRepaymentExperiment || false;
  try {
    // 哈啰出行app&实验初始化状态，走实验
    if (platform.isEasyBike && typeof hitRepaymentExperiment !== 'boolean') {
      const { hitExperiment } = await getRepaymentPageExperiment();
      result = hitExperiment;
      // 更新state
      updateModuleState('base', {
        hitRepaymentExperiment: hitExperiment,
      });
    }
  } catch (error) {
    // 兜底，不影响正常业务
  }
  // 存储到localStorage
  localStorage.setItem('AppFinanceH5Web_hit_RepaymentVersion_experiment', String(result));
  return result;
};

/**
 * 创建路由匹配的正则表达式
 * @param route 路由名称
 * @returns 正则表达式
 */
const createRoutePattern = (route: string): RegExp => new RegExp(`(\\.html#\\/|#\\/)${route.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([?&/]|$)`, 'g');

/**
 * 根据还款流程版本映射路由
 * @param urlOrRouter 原始路由或URL
 * @returns 映射后的路由或URL
 */
export const mapRouteByRepaymentVersion = async (urlOrRouter: string): Promise<string> => {
  // 先检查路由是否在映射表中
  const matchedRoute = Object.entries(routeNameMapping).find(([oldRoute]) => {
    // 如果是完整URL，需要精确匹配路由部分
    if (urlOrRouter.startsWith('http')) {
      // 只有包含AppFinanceLoanH5的URL才进行映射
      if (!urlOrRouter.includes('AppFinanceLoanH5')) {
        return false;
      }
      // 匹配 .html#/路由名 或 #/路由名 的模式
      const routePattern = createRoutePattern(oldRoute);
      return routePattern.test(urlOrRouter);
    }
    // 纯路由名称，直接匹配
    return urlOrRouter === oldRoute;
  });

  // 如果路由不在映射表中，直接返回原路由
  if (!matchedRoute) {
    return urlOrRouter;
  }

  // 路由在映射表中，检查是否命中实验组
  const repaymentVersion = await getAndStoreRepaymentVersion();

  if (!repaymentVersion) {
    return urlOrRouter;
  }

  // 命中实验组，进行路由替换
  const [oldRoute, newRoute] = matchedRoute;
  if (urlOrRouter.startsWith('http')) {
    // 完整URL，需要同时替换HTML文件名和路由部分
    let result = urlOrRouter;

    // 1. 替换HTML文件名（预渲染链接）
    // 检查是否是预渲染链接（包含pr_index_）
    if (result.includes('pr_index_')) {
      // 遍历routeNameMapping，检查pr_index_后面的值是否匹配key
      Object.entries(routeNameMapping).forEach(([oldRouteKey, newRouteKey]) => {
        // 使用更严谨的正则表达式，确保只替换.html前面的HTML文件名
        const htmlPattern = new RegExp(`(pr_index_${oldRouteKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\.html`, 'g');
        if (htmlPattern.test(result)) {
          result = result.replace(htmlPattern, `pr_index_${newRouteKey}.html`);
        }
      });
    }

    // 2. 替换路由部分
    const routePattern = createRoutePattern(oldRoute);
    result = result.replace(routePattern, `$1${newRoute}$2`);

    return result;
  }
  // 纯路由名称，直接替换
  return newRoute;
};
